import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule } from "lucide-angular";
import { Subscription } from "rxjs";
import {
  CustomNotificationService,
  NotificationState,
} from "../../../services/custom-notification.service";

@Component({
  selector: "app-global-notifications",
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="notifications-container">
      <div
        *ngFor="
          let notification of notificationState.notifications;
          trackBy: trackByFn
        "
        class="notification-card"
        [class]="'notification-' + notification.type"
        [@slideIn]
      >
        <div class="notification-header">
          <div class="notification-icon">
            <lucide-icon [name]="notification.icon" [size]="18"></lucide-icon>
          </div>
          <div class="notification-content">
            <h4 class="notification-title">{{ notification.title }}</h4>
            <p class="notification-message">{{ notification.message }}</p>
          </div>
          <button class="notification-close" (click)="remove(notification.id)">
            <span class="close-icon">✕</span>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .notifications-container {
        position: fixed;
        top: calc(100px + env(safe-area-inset-top, 0px));
        right: calc(20px + env(safe-area-inset-right, 0px));
        z-index: 10001;
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-width: 400px;
        width: 100%;
      }

      .notification-card {
        background: white;
        border-radius: 12px;
        padding: 16px;
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        border-left: 4px solid;
        animation: slideIn 0.3s ease-out;
        max-width: 100%;
      }

      .notification-success {
        border-left-color: #10b981;
        background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
      }

      .notification-error {
        border-left-color: #ef4444;
        background: linear-gradient(135deg, #fef2f2 0%, #fef2f2 100%);
      }

      .notification-warning {
        border-left-color: #f59e0b;
        background: linear-gradient(135deg, #fffbeb 0%, #fefce8 100%);
      }

      .notification-info {
        border-left-color: var(--rb-teal);
        background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
      }

      .notification-header {
        display: flex;
        align-items: flex-start;
        gap: 12px;
      }

      .notification-icon {
        font-size: 20px;
        flex-shrink: 0;
        margin-top: 2px;
      }

      .notification-content {
        flex: 1;
        min-width: 0;
      }

      .notification-title {
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 4px 0;
        color: #1e293b;
      }

      .notification-message {
        font-size: 13px;
        color: #64748b;
        margin: 0;
        line-height: 1.4;
        word-wrap: break-word;
      }

      .notification-close {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;
        flex-shrink: 0;
      }

      .notification-close:hover {
        background: rgba(0, 0, 0, 0.1);
      }

      .close-icon {
        font-size: 12px;
        color: #64748b;
      }

      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateX(100%);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      @media (max-width: 768px) {
        .notifications-container {
          top: calc(100px + env(safe-area-inset-top, 0px));
          right: calc(10px + env(safe-area-inset-right, 0px));
          left: calc(10px + env(safe-area-inset-left, 0px));
          max-width: none;
          width: auto;
        }

        .notification-card {
          padding: 12px;
          margin: 0;
          box-sizing: border-box;
        }

        .notification-title {
          font-size: 13px;
        }

        .notification-message {
          font-size: 12px;
        }
      }

      @media (max-width: 480px) {
        .notifications-container {
          top: calc(80px + env(safe-area-inset-top, 0px));
          right: calc(8px + env(safe-area-inset-right, 0px));
          left: calc(8px + env(safe-area-inset-left, 0px));
          max-width: none;
          width: auto;
        }

        .notification-card {
          padding: 10px;
          border-radius: 8px;
        }

        .notification-header {
          gap: 8px;
        }

        .notification-icon {
          font-size: 16px;
        }

        .notification-title {
          font-size: 12px;
        }

        .notification-message {
          font-size: 11px;
        }
      }
    `,
  ],
})
export class GlobalNotificationsComponent implements OnInit, OnDestroy {
  notificationState: NotificationState = { notifications: [] };
  private subscription?: Subscription;

  constructor(private notificationService: CustomNotificationService) {}

  ngOnInit() {
    this.subscription = this.notificationService.notificationState$.subscribe(
      (state) => (this.notificationState = state),
    );
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  remove(id: string) {
    this.notificationService.remove(id);
  }

  trackByFn(index: number, item: any) {
    return item.id;
  }
}
